import { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { apiFetch } from '../utils/api';
import { DollarSign, TrendingUp, Package, AlertTriangle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function HomePage() {
    const [stats, setStats] = useState({
        todaySales: 0,
        monthSales: 0,
        totalOrders: 0
    });
    const [chartData, setChartData] = useState([]);

    useEffect(() => {
        apiFetch(`${API_URL}/api/reports/sales-by-day`)
            .then(res => res.json())
            .then(data => {
                // Calculate stats
                const today = new Date().toISOString().split('T')[0];
                const todayData = data.find(d => d.date.startsWith(today));
                const totalRev = data.reduce((acc, curr) => acc + parseInt(curr.total_amount), 0);
                const totalOrders = data.reduce((acc, curr) => acc + parseInt(curr.total_sales), 0);

                setStats({
                    todaySales: todayData ? parseInt(todayData.total_amount) : 0,
                    monthSales: totalRev,
                    totalOrders: totalOrders
                });

                // Format data for chart (last 7 days usually looks best, or all if few)
                const formattedChartData = data.map(item => ({
                    name: new Date(item.date).toLocaleDateString('es-CL', { weekday: 'short', day: 'numeric' }),
                    sales: parseInt(item.total_amount)
                })).reverse(); // API usually returns DESC by date, we want ASC for chart L-R

                setChartData(formattedChartData);
            })
            .catch(console.error);
    }, []);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            maximumFractionDigits: 0
        }).format(amount);
    };

    return (
        <div className="dashboard">
            {/* Stats Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>

                <div className="card stat-card flex items-center justify-between">
                    <div>
                        <div className="text-sm text-muted">Ventas de Hoy</div>
                        <div className="stat-value">{formatCurrency(stats.todaySales)}</div>
                    </div>
                    <div className="stat-icon stat-icon-primary">
                        <DollarSign size={24} aria-hidden />
                    </div>
                </div>

                <div className="card stat-card flex items-center justify-between">
                    <div>
                        <div className="text-sm text-muted">Ingresos (30d)</div>
                        <div className="stat-value">{formatCurrency(stats.monthSales)}</div>
                    </div>
                    <div className="stat-icon stat-icon-success">
                        <TrendingUp size={24} aria-hidden />
                    </div>
                </div>

                <div className="card stat-card flex items-center justify-between">
                    <div>
                        <div className="text-sm text-muted">Total Órdenes (30d)</div>
                        <div className="stat-value">{stats.totalOrders}</div>
                    </div>
                    <div className="stat-icon stat-icon-accent">
                        <Package size={24} aria-hidden />
                    </div>
                </div>
            </div>

            <div className="card" style={{ height: '400px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ marginBottom: '2rem' }}>
                    <h2 style={{ fontSize: '1.1rem', margin: 0 }}>Resumen de Ventas</h2>
                    <p className="text-muted text-sm mt-1">Rendimiento diario</p>
                </div>

                <div style={{ width: '100%', height: '300px' }}>
                    {chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartData}>
                                <defs>
                                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#F5B700" stopOpacity={0.35} />
                                        <stop offset="95%" stopColor="#F5B700" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7e5e4" />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#57534e', fontSize: 12 }}
                                    dy={10}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#57534e', fontSize: 12 }}
                                    tickFormatter={(value) => `$${value}`}
                                />
                                <Tooltip
                                    contentStyle={{ borderRadius: '8px', border: '1px solid #e7e5e4', boxShadow: '0 4px 12px rgba(28, 25, 23, 0.08)' }}
                                    formatter={(value) => [formatCurrency(value), 'Revenue']}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="sales"
                                    stroke="#F5B700"
                                    strokeWidth={3}
                                    fillOpacity={1}
                                    fill="url(#colorSales)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#78716c' }}>
                            No hay datos disponibles
                        </div>
                    )}
                </div>
            </div>

            {/* Expiring Products Alert */}
            <div className="card mt-6">
                <div style={{ marginBottom: '1rem' }} className="flex justify-between items-center">
                    <div>
                        <h2 className="flex items-center gap-2" style={{ fontSize: '1.1rem', margin: 0, color: 'var(--danger-color)' }}>
                            <AlertTriangle size={20} aria-hidden /> Alertas de Vencimiento
                        </h2>
                        <p className="text-muted text-sm mt-1">Productos que vencen en los próximos 30 días</p>
                    </div>
                </div>
                <AlertsWidget />
            </div>
        </div>
    );
}

function AlertsWidget() {
    const [alerts, setAlerts] = useState([]);

    useEffect(() => {
        apiFetch(`${API_URL}/api/reports/alerts`)
            .then(res => res.json())
            .then(data => {
                console.log('Alertas recibidas:', data);
                // Debug: verificar cantidad_total
                data.forEach(item => {
                    if (item.cantidad_total === 0 || item.cantidad_total === null || item.cantidad_total === undefined) {
                        console.warn('Producto con cantidad 0/null:', item.nombre, 'cantidad_total:', item.cantidad_total);
                    }
                });
                setAlerts(data);
            })
            .catch(console.error);
    }, []);

    if (alerts.length === 0) return <div className="text-gray-500 italic">No hay alertas pendientes.</div>;

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left">
                <thead>
                    <tr className="border-b text-sm text-gray-500">
                        <th className="py-2">Producto</th>
                        <th className="py-2">Código</th>
                        <th className="py-2">Cantidad</th>
                        <th className="py-2">Vence el</th>
                        <th className="py-2">Días Restantes</th>
                    </tr>
                </thead>
                <tbody>
                    {alerts.map(a => {
                        const daysLeft = Math.ceil((new Date(a.fecha_vencimiento) - new Date()) / (1000 * 60 * 60 * 24));
                        // Ensure cantidad_total is properly parsed
                        const cantidad = a.cantidad_total != null && a.cantidad_total !== undefined 
                            ? (typeof a.cantidad_total === 'string' ? parseInt(a.cantidad_total, 10) : a.cantidad_total)
                            : 0;
                        return (
                            <tr key={a.id} className="border-b hover:bg-gray-50">
                                <td className="py-2 font-medium">{a.nombre}</td>
                                <td className="py-2 text-gray-500 text-sm">{a.codigo_barra}</td>
                                <td className="py-2 font-semibold" style={{ color: 'var(--primary-color)' }}>
                                    {cantidad} unidades
                                </td>
                                <td className="py-2">{new Date(a.fecha_vencimiento).toLocaleDateString()}</td>
                                <td className={`py-2 font-bold ${daysLeft <= 7 ? 'text-red-600' : 'text-yellow-600'}`}>
                                    {daysLeft} días
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
